import { set, useCurrentElement } from "@vueuse/core";
import { ref, shallowRef, toValue, watch } from "vue";
import { useStepperStore } from "../../stores/stepper.store.js";
import type { Step } from "../../stores/store.type.js";

export function useParentStep() {
	const currentElement = useCurrentElement<HTMLElement>();
	const parentStepId = ref<string | null>(null);
	const parentStep = ref<Step | null>(null);
	const isActiveStep = ref<boolean>(false);
	const isSubmitted = ref(false);

	function findParentStep() {
		return useStepperStore()
			.getState()
			.steps.steps.find((step: Step) => step.stepId === toValue(parentStepId));
	}

	function findParentStepId() {
		const parentElement = currentElement.value?.closest(".product-config-step");
		set(parentStepId, parentElement?.id ?? null);
		set(parentStep, findParentStep());
	}

	watch(currentElement, () => {
		findParentStepId();
	});

	findParentStepId();

	useStepperStore().subscribe((state) => {
		set(isActiveStep, state.steps.activeStep === toValue(parentStepId));
		const parentStepItem: Step | null = toValue(parentStep);
		if (parentStepItem) {
			set(isSubmitted, parentStepItem.isSubmitted);
		}
	});

	return { parentStepId, parentStep, findParentStepId, isActiveStep, isSubmitted };
}

export function useParentStepElement() {
	const { parentStepId } = useParentStep();

	const parentStepElement = shallowRef<HTMLElement | null>(null);

	function findParentStepElement(): HTMLElement | null {
		const elementId = toValue(parentStepId);
		if (elementId) {
			return document.getElementById(elementId);
		}
		return null;
	}

	watch(parentStepId, () => set(parentStepElement, findParentStepElement()), { immediate: true });

	return { parentStepElement };
}
